import type { IResource } from "@gettactic/api";
import { getManagedRegionShapeIds } from "@gettactic/helpers";
import {
  useAuthenticated,
  useFloorSpacesQuery,
  useMeQuery
} from "@gettactic/hooks";

/**
 * Whether the person can edit desks on this floor as a zone manager: they
 * are not an admin (admins get the full builder) and manage the zone of at
 * least one region on the floor's active map. Fetches nothing for admins,
 * and only loads the floor's regions once they manage a zone at its office.
 */
export const useCanEditManagedZones = (
  floor: IResource | null | undefined
): boolean => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const isAdmin = !!authenticatedUser?.permissions?.settings;

  const { data: me } = useMeQuery({ enabled: !isAdmin && !!floor });
  const officeId = floor?.office_id ?? "";
  const managedZones = me?.managed_zones ?? [];
  const managesAtOffice =
    !isAdmin && managedZones.some((managed) => managed.office_id === officeId);

  const { data: spaces } = useFloorSpacesQuery(
    managesAtOffice && floor ? floor.id : null
  );
  if (!managesAtOffice || !spaces) return false;

  return getManagedRegionShapeIds(spaces, officeId, managedZones).length > 0;
};
